import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { apiClient, type VerificationDocument } from '@theraply/api-client';

interface DocumentViewerProps {
  document: VerificationDocument;
  onClose: () => void;
}

export default function DocumentViewer({ document, onClose }: DocumentViewerProps) {
  const [zoom, setZoom] = useState(100);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(true);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchPresignedUrl() {
      try {
        setUrlLoading(true);
        setUrlError(null);
        const response = await apiClient.get<{ url: string }>(
          `/therapist/documents/${document.id}/download`
        );
        if (!cancelled) {
          setPresignedUrl(response.data.url);
        }
      } catch (err: any) {
        if (!cancelled) {
          setUrlError(err.response?.data?.message || 'Failed to load document URL');
        }
      } finally {
        if (!cancelled) {
          setUrlLoading(false);
        }
      }
    }

    fetchPresignedUrl();

    return () => {
      cancelled = true;
    };
  }, [document.id]);

  const handleDownload = async () => {
    if (!presignedUrl) return;

    try {
      const response = await fetch(presignedUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.fileName;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);
    } catch {
      // Fallback: open in new tab
      window.open(presignedUrl, '_blank');
    }
  };

  const isImage = document.contentType.startsWith('image/');
  const isPdf = document.contentType === 'application/pdf';

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full max-w-6xl">
                {/* Header */}
                <div className="bg-white px-4 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 truncate">
                        {document.fileName}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        {document.documentType.replace(/_/g, ' ')} • {(document.fileSize / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <div className="ml-3 flex items-center space-x-2">
                      {/* Zoom controls for images */}
                      {isImage && (
                        <div className="flex items-center space-x-2 mr-4">
                          <button
                            onClick={() => setZoom(Math.max(50, zoom - 25))}
                            className="p-2 text-gray-400 hover:text-gray-500"
                            disabled={zoom <= 50}
                          >
                            <span className="sr-only">Zoom out</span>
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                            </svg>
                          </button>
                          <span className="text-sm text-gray-600 w-12 text-center">{zoom}%</span>
                          <button
                            onClick={() => setZoom(Math.min(200, zoom + 25))}
                            className="p-2 text-gray-400 hover:text-gray-500"
                            disabled={zoom >= 200}
                          >
                            <span className="sr-only">Zoom in</span>
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </button>
                        </div>
                      )}

                      {/* Download button */}
                      <button
                        type="button"
                        onClick={handleDownload}
                        disabled={!presignedUrl}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                        Download
                      </button>

                      {/* Close button */}
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Document Preview */}
                <div className="bg-gray-100 px-4 py-6 sm:p-6">
                  <div className="flex gap-6">
                    {/* Main preview */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm overflow-auto" style={{ maxHeight: '600px' }}>
                      {urlLoading ? (
                        <div className="p-4 flex items-center justify-center py-12">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                            <p className="mt-2 text-sm text-gray-500">Loading document...</p>
                          </div>
                        </div>
                      ) : urlError ? (
                        <div className="p-4 text-center py-12">
                          <p className="text-sm text-red-600">{urlError}</p>
                          <p className="mt-1 text-sm text-gray-500">Use the download button if available.</p>
                        </div>
                      ) : isImage && presignedUrl ? (
                        <div className="p-4 flex items-center justify-center">
                          <img
                            src={presignedUrl}
                            alt={document.fileName}
                            style={{ width: `${zoom}%` }}
                            className="max-w-full h-auto"
                          />
                        </div>
                      ) : isPdf && presignedUrl ? (
                        <iframe
                          src={presignedUrl}
                          title={document.fileName}
                          className="w-full border-0"
                          style={{ height: '600px' }}
                        />
                      ) : (
                        <div className="p-4">
                          <div className="text-center py-12">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">Document Preview Unavailable</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Download the file to view its contents
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Extracted data panel */}
                    {document.extractedData && Object.keys(document.extractedData).length > 0 && (
                      <div className="w-80 bg-white rounded-lg shadow-sm p-4 overflow-auto" style={{ maxHeight: '600px' }}>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Extracted Data</h4>
                        <dl className="space-y-3">
                          {Object.entries(document.extractedData).map(([key, value]) => (
                            <div key={key}>
                              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {key.replace(/_/g, ' ')}
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                              </dd>
                              {/* Confidence indicator if available */}
                              {typeof value === 'object' && value !== null && 'confidence' in value && (
                                <div className="mt-1">
                                  <div className="flex items-center">
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className="bg-green-500 h-1.5 rounded-full"
                                        style={{ width: `${Number(value.confidence) * 100}%` }}
                                      />
                                    </div>
                                    <span className="ml-2 text-xs text-gray-500">
                                      {(Number(value.confidence) * 100).toFixed(0)}%
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </dl>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
